import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { RootState } from '../state';
import { replaceStringTagsReact } from '../utils/reactUtils';

const missingSymbolsSent: string[] = [];

interface I18n {
  t: (key: string, noFallback?: boolean) => any;
  tOr: (key: string, fallback: any) => any;
  jsxT: (key: string, props?: any) => any;
  hasTranslations: boolean;
  addSymbols: (symbols: any) => void;
  translateWithVars: (
    key: string,
    variables?: Record<string, string | number>,
    noFallback?: boolean,
  ) => string;
}
export function useI18n(): I18n {
  const translationsSymbols = useSelector(
    (state: RootState) => state.translations,
  );
  const hasTranslations =
    !!translationsSymbols && !!Object.keys(translationsSymbols).length;
  const cacheTranslations = translationsSymbols?._cached_;
  const t = (key: string, noFallback: boolean = false) => {
    if (
      hasTranslations &&
      !noFallback &&
      !translationsSymbols?.[key] &&
      !missingSymbolsSent.includes(key) &&
      !cacheTranslations
    ) {
      missingSymbolsSent.push(key);
      Sentry.captureMessage(`missing symbol:${key}`);
    }
    const val =
      translationsSymbols?.[key] ||
      (noFallback ? '' : `missing symbol: ${key}`);
    return replaceStringTagsReact(val);
  };
  /**
   * A translation with a hard-coded default, for copy that must never render
   * blank while the symbol is being added to the CMS.
   *
   * `t(key, true) || fallback` does NOT do this: `t` pipes its result through
   * `replaceStringTagsReact`, and parsing an empty string returns an empty
   * ARRAY, which is truthy — so the fallback never fires and the player sees
   * nothing at all. Test the raw symbol instead.
   */
  const tOr = (key: string, fallback: any) => {
    const val = translationsSymbols?.[key];
    return val ? replaceStringTagsReact(val) : fallback;
  };
  const jsxT = (key: string, props: any = {}) => {
    if (
      hasTranslations &&
      !translationsSymbols?.[key] &&
      !missingSymbolsSent.includes(key) &&
      !cacheTranslations
    ) {
      missingSymbolsSent.push(key);
      Sentry.captureMessage(`missing symbol:${key}`);
    }
    const val = translationsSymbols?.[key] || `missing symbol: ${key}`;
    return replaceStringTagsReact(val, props);
  };

  const translateWithVars = (
    key: string,
    variables?: Record<string, string | number>,
    noFallback: boolean = false,
  ): string => {
    let translation = t(key, noFallback) as string;
    if (translation.includes('missing')) {
      return translation;
    }
    if (variables) {
      const variableKeys = Object.keys(variables);
      let missingVars: string[] = [];
      variableKeys.forEach(varKey => {
        const varPattern = new RegExp(`{${varKey}}`, 'g');
        if (varPattern.test(translation)) {
          translation = translation.replace(
            varPattern,
            variables[varKey].toString(),
          );
        } else {
          missingVars.push(varKey);
        }
      });
      if (missingVars.length > 0) {
        missingVars.forEach(varKey => {
          translation = `Symbol ${key} is missing it's varible. Ensure the translation contains {${varKey}}.`;
        });
      }
    }

    return translation;
  };

  return {
    hasTranslations,
    t,
    tOr,
    jsxT,
    addSymbols: () => {},
    translateWithVars,
  };
}
